// Relatório financeiro de faturas (PO1-3745), gerado no navegador com jsPDF em
// A4 retrato, no molde do PDF da Auditoria do Pedido (AuditoriaPedido/relatorioPdf.js).
// Uma seção por grupo (Pendente / Liberado para pagar / Pago), com subtotal, e
// o total geral no fim. O conteúdo é montado por `montarRelatorio` (função pura,
// testável sem jsPDF) e desenhado por `montarPdfFinanceiro`.
import { jsPDF } from "jspdf";
import { COR_TEXTO, COR_LABEL } from "views/Empresa/Indicadores/Emissoes/relatorioPdf";
import { fmtBRL, fmtInt, fmtData, fmtDataPura, fmtDataBRT, labelSituacao } from "views/Empresa/ConferenciaFatura/formatters";
import { carregarLogo } from "views/Empresa/AuditoriaPedido/relatorioPdf";
import { GRUPOS } from "./grupos";

const MARGEM = 15;
// Paleta da marca Everlog (mesma das propostas comerciais e do PDF da Auditoria).
const NAVY = [20, 57, 90];
const AZUL = [27, 135, 201];
const VERDE = [141, 198, 63];
const LOGO_PROPORCAO = 3484 / 1023;

// Teto de linhas por grupo. Acima disso a tela pede para filtrar o período em
// vez de gerar um PDF de centenas de páginas (a Fastline tem >5 mil Integradas).
export const LIMITE_LINHAS_POR_GRUPO = 1000;

export const nomeArquivoPdf = (embarcador, data = new Date()) => {
  const slug = String(embarcador || "embarcador")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Za-z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
  const d = data instanceof Date ? data : new Date(data);
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return `financeiro-faturas-${slug || "embarcador"}-${iso}.pdf`;
};

const ROTULO_DATA_CAMPO = {
  emissao: "Emissão",
  entrada: "Entrada",
  vencimento: "Vencimento",
  integracao: "Integração",
  pagamento: "Pagamento",
};

// Texto dos filtros para o cabeçalho ("Vencimento de 01/10/2026 a 31/10/2026 · Transportadora X").
export function descreverFiltros(filtros = {}) {
  const partes = [];
  if (filtros.dataCampo && (filtros.dataDe || filtros.dataAte)) {
    const rotulo = ROTULO_DATA_CAMPO[filtros.dataCampo] || filtros.dataCampo;
    const de = filtros.dataDe ? fmtDataPura(filtros.dataDe) : null;
    const ate = filtros.dataAte ? fmtDataPura(filtros.dataAte) : null;
    if (de && ate) partes.push(`${rotulo} de ${de} a ${ate}`);
    else if (de) partes.push(`${rotulo} a partir de ${de}`);
    else partes.push(`${rotulo} até ${ate}`);
  }
  if (filtros.transportadoraNome) partes.push(`Transportadora ${filtros.transportadoraNome}`);
  if (filtros.nrFaturaExt) partes.push(`Fatura ${filtros.nrFaturaExt}`);
  return partes.length ? partes.join(" · ") : "Todas as faturas em aberto e pagas";
}

// Data mostrada na coluna variável de cada grupo (mesma regra da tela).
export function dataDoGrupo(grupo, fatura) {
  if (!grupo || !grupo.colunaData || !fatura) return "-";
  const v = fatura[grupo.colunaData.campo];
  if (!v) return "-";
  return grupo.colunaData.formato === "dataBRT" ? fmtDataBRT(v) : fmtData(v);
}

// Monta as seções do relatório a partir das linhas por grupo. Função pura.
// `porGrupo` = { PENDENTE: [...faturas], LIBERADO: [...], PAGO: [...] }.
export function montarRelatorio(porGrupo = {}) {
  const secoes = GRUPOS.map((g) => {
    const linhas = porGrupo[g.key] || [];
    const valor = linhas.reduce((acc, f) => acc + (Number(f.valTotalFatura) || 0), 0);
    return {
      key: g.key,
      label: g.label,
      colunaData: g.colunaData.label,
      qtd: linhas.length,
      valor: Math.round(valor * 100) / 100,
      linhas: linhas.map((f) => ({
        fatura: f.numFaturaExterna || String(f.id || "-"),
        transportadora: f.transportadoraFantasia || "-",
        emissao: f.dataEmissaoExterna ? fmtData(f.dataEmissaoExterna) : "-",
        vencimento: f.dataVencimentoExterna ? fmtDataPura(f.dataVencimentoExterna) : "-",
        // Rótulo controlado pelo front: Integrada sai como "Integrada", nunca "Paga".
        situacao: labelSituacao(f.situacaoCodigo, f.situacaoNome),
        valor: Number(f.valTotalFatura) || 0,
        data: dataDoGrupo(g, f),
      })),
    };
  });
  const total = secoes.reduce(
    (acc, s) => ({ qtd: acc.qtd + s.qtd, valor: acc.valor + s.valor }),
    { qtd: 0, valor: 0 }
  );
  total.valor = Math.round(total.valor * 100) / 100;
  return { secoes, total };
}

function checarQuebra(doc, y, necessario, aoQuebrar) {
  const pageH = doc.internal.pageSize.getHeight();
  if (y + necessario > pageH - 16) {
    doc.addPage();
    let novoY = 20;
    if (aoQuebrar) novoY = aoQuebrar(novoY);
    return novoY;
  }
  return y;
}

function titulo(doc, texto, y) {
  doc.setFillColor(...VERDE);
  doc.rect(MARGEM, y - 3.6, 1.2, 4.6, "F");
  doc.setTextColor(...NAVY);
  doc.setFont(undefined, "bold");
  doc.setFontSize(12);
  doc.text(texto, MARGEM + 3.5, y);
  doc.setFont(undefined, "normal");
  return y + 7;
}

function cabecalhoTabela(doc, y, colunas) {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setFillColor(...NAVY);
  doc.rect(MARGEM, y - 4, pageW - 2 * MARGEM, 6.5, "F");
  doc.setFontSize(7.5);
  doc.setTextColor(255, 255, 255);
  colunas.forEach((c) =>
    doc.text(c.label.toUpperCase(), c.x + (c.align ? 0 : 1), y, c.align ? { align: c.align } : undefined)
  );
  doc.setFontSize(8.5);
  doc.setTextColor(...COR_TEXTO);
  return y + 7;
}

// Tabela com repetição do cabeçalho a cada quebra de página.
function tabela(doc, y, colunas, linhas) {
  y = checarQuebra(doc, y, 16);
  y = cabecalhoTabela(doc, y, colunas);
  linhas.forEach((linha) => {
    y = checarQuebra(doc, y, 6, (novoY) => cabecalhoTabela(doc, novoY, colunas));
    colunas.forEach((c, i) => {
      const v = linha[i] != null ? String(linha[i]) : "-";
      const texto = c.largura ? doc.splitTextToSize(v, c.largura)[0] : v;
      doc.text(texto, c.x, y, c.align ? { align: c.align } : undefined);
    });
    y += 5.2;
  });
  return y + 2;
}

function subtotal(doc, y, texto, valor) {
  const pageW = doc.internal.pageSize.getWidth();
  y = checarQuebra(doc, y, 10);
  doc.setDrawColor(215, 222, 232);
  doc.setLineWidth(0.2);
  doc.line(MARGEM, y - 3, pageW - MARGEM, y - 3);
  doc.setFont(undefined, "bold");
  doc.setFontSize(9);
  doc.setTextColor(...NAVY);
  doc.text(texto, MARGEM, y + 1);
  doc.text(fmtBRL(valor), pageW - MARGEM, y + 1, { align: "right" });
  doc.setFont(undefined, "normal");
  doc.setTextColor(...COR_TEXTO);
  return y + 9;
}

function cartoes(doc, y, itens) {
  const pageW = doc.internal.pageSize.getWidth();
  const gap = 3;
  const w = (pageW - 2 * MARGEM - gap * (itens.length - 1)) / itens.length;
  itens.forEach((it, i) => {
    const x = MARGEM + i * (w + gap);
    doc.setDrawColor(215, 222, 232);
    doc.setFillColor(246, 248, 251);
    doc.roundedRect(x, y, w, 19, 2, 2, "FD");
    doc.setFillColor(...(it.destaque || AZUL));
    doc.rect(x, y + 2, 1, 15, "F");
    doc.setFontSize(7.5);
    doc.setTextColor(...COR_LABEL);
    doc.text(it.label.toUpperCase(), x + 3, y + 5.5);
    doc.setFontSize(11);
    doc.setFont(undefined, "bold");
    doc.setTextColor(...COR_TEXTO);
    doc.text(it.valor, x + 3, y + 11.5);
    doc.setFont(undefined, "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...COR_LABEL);
    doc.text(it.sub, x + 3, y + 16.3);
  });
  return y + 25;
}

// `dados` = { embarcador, filtros, porGrupo }. `porGrupo` como em montarRelatorio.
export function montarPdfFinanceiro(dados, { logo = null, agora = new Date() } = {}) {
  const { secoes, total } = montarRelatorio(dados.porGrupo);
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  // Cabeçalho: logo à esquerda, título à direita, faixa azul + verde.
  if (logo) {
    doc.addImage(logo, "JPEG", MARGEM, 9, 16 * LOGO_PROPORCAO * 0.75, 12);
  } else {
    doc.setTextColor(...NAVY);
    doc.setFont(undefined, "bold");
    doc.setFontSize(18);
    doc.text("Everlog", MARGEM, 18);
    doc.setFont(undefined, "normal");
  }
  doc.setTextColor(...NAVY);
  doc.setFont(undefined, "bold");
  doc.setFontSize(15);
  doc.text("Relatório financeiro de faturas", pageW - MARGEM, 15, { align: "right" });
  doc.setFont(undefined, "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...COR_LABEL);
  doc.text(dados.embarcador || "", pageW - MARGEM, 21, { align: "right" });
  doc.setFillColor(...AZUL);
  doc.rect(0, 27, pageW * 0.8, 1.6, "F");
  doc.setFillColor(...VERDE);
  doc.rect(pageW * 0.8, 27, pageW * 0.2, 1.6, "F");

  doc.setFontSize(8.5);
  doc.setTextColor(...COR_LABEL);
  doc.text(`Filtros: ${descreverFiltros(dados.filtros)}`, MARGEM, 34);
  doc.text(`Emitido em ${agora.toLocaleString("pt-BR")}`, pageW - MARGEM, 34, { align: "right" });

  let y = 40;
  const corGrupo = { PENDENTE: [161, 98, 7], LIBERADO: AZUL, PAGO: VERDE };
  y = cartoes(
    doc,
    y,
    secoes.map((s) => ({
      label: s.label,
      valor: fmtBRL(s.valor),
      sub: `${fmtInt(s.qtd)} fatura(s)`,
      destaque: corGrupo[s.key],
    }))
  );

  const colunas = [
    { label: "Fatura", x: MARGEM, largura: 26 },
    { label: "Transportadora", x: 44, largura: 46 },
    { label: "Emissão", x: 92 },
    { label: "Vencimento", x: 112 },
    { label: "Situação", x: 133, largura: 24 },
    { label: "Valor", x: pageW - MARGEM, align: "right" },
  ];

  secoes.forEach((s) => {
    const colunasDoGrupo = colunas.slice();
    // A coluna variável (Entrada / Liberada em / Paga em) entra antes do valor.
    colunasDoGrupo.splice(5, 0, { label: s.colunaData, x: 158 });
    y = checarQuebra(doc, y, 30);
    y = titulo(doc, `${s.label} · ${fmtInt(s.qtd)} fatura(s)`, y);
    if (s.linhas.length) {
      y = tabela(
        doc,
        y,
        colunasDoGrupo,
        s.linhas.map((l) => [l.fatura, l.transportadora, l.emissao, l.vencimento, l.situacao, l.data, fmtBRL(l.valor)])
      );
    } else {
      doc.setFontSize(9);
      doc.setTextColor(...COR_LABEL);
      doc.text("Nenhuma fatura neste grupo com os filtros atuais.", MARGEM, y);
      doc.setTextColor(...COR_TEXTO);
      y += 7;
    }
    y = subtotal(doc, y, `Subtotal ${s.label}`, s.valor);
    y += 3;
  });

  y = checarQuebra(doc, y, 14);
  const pageWInner = pageW - 2 * MARGEM;
  doc.setFillColor(...NAVY);
  doc.roundedRect(MARGEM, y - 4, pageWInner, 10, 1.5, 1.5, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont(undefined, "bold");
  doc.setFontSize(10);
  doc.text(`Total geral · ${fmtInt(total.qtd)} fatura(s)`, MARGEM + 3, y + 2.5);
  doc.text(fmtBRL(total.valor), pageW - MARGEM - 3, y + 2.5, { align: "right" });
  doc.setFont(undefined, "normal");

  const pageH = doc.internal.pageSize.getHeight();
  const totalPaginas = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPaginas; p += 1) {
    doc.setPage(p);
    doc.setDrawColor(215, 222, 232);
    doc.setLineWidth(0.2);
    doc.line(MARGEM, pageH - 12, pageW - MARGEM, pageH - 12);
    doc.setTextColor(...COR_LABEL);
    doc.setFontSize(8);
    doc.text(
      `Documento gerado em ${agora.toLocaleString("pt-BR")} · Everlog Brasil · página ${p} de ${totalPaginas}`,
      MARGEM,
      pageH - 8
    );
  }
  return doc;
}

export async function baixarPdfFinanceiro(dados) {
  const logo = await carregarLogo();
  const agora = new Date();
  montarPdfFinanceiro(dados, { logo, agora }).save(nomeArquivoPdf(dados.embarcador, agora));
}
